/**
 * CombatScreen — Tacticus-style turn-based RPG combat.
 * Self-contained React component. Integrates with engine.js + enemies.js.
 *
 * Props:
 *   characterId  {string}   — one of: mage, knight, archer, scholar, bard
 *   onXPEarned   {function} — called with xp (number) when a battle ends
 *   onBack       {function} — called when user exits to dashboard
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  generateQuestion,
  calculateDamage,
  calculateBlock,
  checkWinCondition,
  awardXP,
  applyStatusEffect,
  removeStatusEffect,
  hasEffect,
  isPrime,
  EFFECTS,
  DEFAULT_CHARACTERS,
} from "./engine";
import { ZONES, getRandomEnemy } from "./enemies";

// ─── Theme (mirrors App.js) ───────────────────────────────────────────────────
const PX  = "'Press Start 2P', monospace";
const NU  = "'Nunito', sans-serif";
const C   = {
  bg:        "#0d0a1a",
  bgCard:    "#1a1035",
  bgFlat:    "#130d26",
  bgAlt:     "#0f0b1e",
  border:    "#3b2878",
  shadow:    "#06030f",
  text:      "#e2d4ff",
  textSub:   "#9b80d4",
  textDim:   "#4a3668",
  gold:      "#fbbf24",
  green:     "#34d399",
  greenBg:   "#042819",
  red:       "#f87171",
  redBg:     "#2d0a0a",
  purple:    "#c4b5fd",
  purpleMid: "#7c3aed",
};

// ─── Zone gradient backgrounds ────────────────────────────────────────────────
const ZONE_GRADIENT = {
  meadow:  "linear-gradient(180deg, #3ab5e6 0%, #6dcff6 30%, #a8e063 70%, #22c55e 100%)",
  forest:  "linear-gradient(180deg, #0d1f0f 0%, #1a3d1f 35%, #1e5c2a 65%, #145c20 100%)",
  castle:  "linear-gradient(180deg, #0e0620 0%, #1e0a40 40%, #2d1060 70%, #1a0835 100%)",
  volcano: "linear-gradient(180deg, #1a0505 0%, #7f1d1d 40%, #c2410c 72%, #7c2d12 100%)",
};
const ZONE_GROUND = {
  meadow:  "#15803d",
  forest:  "#14532d",
  castle:  "#2e1065",
  volcano: "#431407",
};

// ─── Tiny helpers ─────────────────────────────────────────────────────────────
const clamp        = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const hpPct        = (hp, max)   => clamp(Math.round((hp / max) * 100), 0, 100);
const hpColor      = (pct)       => pct > 50 ? C.green  : pct > 25 ? C.gold   : C.red;
const enemyHpColor = (pct)       => pct > 50 ? "#f97316": pct > 25 ? "#ef4444": "#7f1d1d";

// ─── Combat phases ────────────────────────────────────────────────────────────
const PH = {
  ZONE_SELECT:     "zone_select",
  PLAYER_CHOICE:   "player_choice",   // waiting for Attack / Ability / Flee
  PLAYER_QUESTION: "player_question", // player answering to attack
  ENEMY_QUESTION:  "enemy_question",  // player answering to block
  ANIMATING:       "animating",       // brief pause between turns
  VICTORY:         "victory",
  DEFEAT:          "defeat",
};

const QUESTION_TIMEOUT_MS = 20000; // 20 s hard limit

// ─────────────────────────────────────────────────────────────────────────────
export default function CombatScreen({ characterId = "mage", onXPEarned, onBack }) {
  // resolved character template
  const charTemplate = DEFAULT_CHARACTERS.find(c => c.id === characterId) || DEFAULT_CHARACTERS[0];

  // ── core state ──────────────────────────────────────────────────────────────
  const [phase,       setPhase]       = useState(PH.ZONE_SELECT);
  const [zone,        setZone]        = useState(null);
  const [player,      setPlayer]      = useState(null);
  const [enemy,       setEnemy]       = useState(null);
  const [question,    setQuestion]    = useState(null);
  const [inputVal,    setInputVal]    = useState("");
  const [combatLog,   setCombatLog]   = useState([]);
  const [abilityUsed, setAbilityUsed] = useState(false);
  const [xpEarned,    setXpEarned]    = useState(0);
  const [correctCount,setCorrectCount]= useState(0);
  const [totalMs,     setTotalMs]     = useState(0);
  const [lastDifficulty, setLastDifficulty] = useState(1);

  // animation flags
  const [enemyShake,  setEnemyShake]  = useState(false);
  const [playerShake, setPlayerShake] = useState(false);
  const [flashMsg,    setFlashMsg]    = useState(null);   // { text, color }

  // question timer
  const questionStartRef = useRef(null);
  const [timeLeft,    setTimeLeft]    = useState(QUESTION_TIMEOUT_MS);
  const timerRef      = useRef(null);
  const inputRef      = useRef(null);

  // ── log helper ──────────────────────────────────────────────────────────────
  const addLog = useCallback((msg) => {
    setCombatLog(prev => [msg, ...prev].slice(0, 5));
  }, []);

  const flash = useCallback((text, color = C.gold) => {
    setFlashMsg({ text, color });
    setTimeout(() => setFlashMsg(null), 1200);
  }, []);

  // ── timer tick ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (phase !== PH.PLAYER_QUESTION && phase !== PH.ENEMY_QUESTION) {
      clearInterval(timerRef.current);
      return;
    }
    questionStartRef.current = questionStartRef.current || Date.now();
    timerRef.current = setInterval(() => {
      const elapsed = Date.now() - questionStartRef.current;
      const left    = Math.max(0, QUESTION_TIMEOUT_MS - elapsed);
      setTimeLeft(left);
      if (left === 0) {
        clearInterval(timerRef.current);
        handleTimeout();
      }
    }, 100);
    return () => clearInterval(timerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // focus input when question appears
  useEffect(() => {
    if ((phase === PH.PLAYER_QUESTION || phase === PH.ENEMY_QUESTION) && inputRef.current) {
      inputRef.current.focus();
    }
  }, [phase]);

  // ── zone select → start battle ───────────────────────────────────────────
  function startBattle(z) {
    const freshPlayer = {
      ...charTemplate,
      hp:            charTemplate.hp,
      activeEffects: [],
    };
    const freshEnemy = getRandomEnemy(z.id);
    setZone(z);
    setPlayer(freshPlayer);
    setEnemy(freshEnemy);
    setCombatLog([`${freshEnemy.name} blocks your path — prove your maths skills!`]);
    setAbilityUsed(false);
    setXpEarned(0);
    setCorrectCount(0);
    setTotalMs(0);
    setPhase(PH.PLAYER_CHOICE);
  }

  // ── player clicks ATTACK ──────────────────────────────────────────────────
  function handleAttack() {
    if (phase !== PH.PLAYER_CHOICE) return;
    const q = generateQuestion(enemy.yearGroup, enemy.topic);
    setQuestion(q);
    setInputVal("");
    setLastDifficulty(q.difficulty);
    questionStartRef.current = Date.now();
    setTimeLeft(QUESTION_TIMEOUT_MS);
    setPhase(PH.PLAYER_QUESTION);
  }

  // ── player clicks USE ABILITY ─────────────────────────────────────────────
  function handleAbility() {
    if (phase !== PH.PLAYER_CHOICE || abilityUsed) return;
    const effect = charTemplate.ability.effect;
    setPlayer(prev => applyStatusEffect(prev, effect));
    setAbilityUsed(true);
    addLog(`${charTemplate.name} activates ${charTemplate.ability.name}!`);
    flash(`${charTemplate.ability.name}!`, C.purple);
    // If ability adds an attack buff, go straight to attack question
    if (effect === EFFECTS.MULTIPLY || effect === EFFECTS.PRIME_SHIELD) {
      const q = generateQuestion(enemy.yearGroup, enemy.topic);
      setQuestion(q);
      setInputVal("");
      setLastDifficulty(q.difficulty);
      questionStartRef.current = Date.now();
      setTimeLeft(QUESTION_TIMEOUT_MS);
      setTimeout(() => setPhase(PH.PLAYER_QUESTION), 600);
    } else {
      // Debuffs applied to enemy, still need to attack
      if (effect === EFFECTS.DIVIDE) {
        setEnemy(prev => applyStatusEffect(prev, EFFECTS.DIVIDE));
        addLog(`${enemy.name}'s defence crumbles — weakened!`);
      } else if (effect === EFFECTS.NEGATIVE) {
        setEnemy(prev => applyStatusEffect(prev, EFFECTS.NEGATIVE));
        addLog(`${enemy.name} is baffled — reduced to simple sums!`);
      }
      const q = generateQuestion(enemy.yearGroup, enemy.topic);
      setQuestion(q);
      setInputVal("");
      setLastDifficulty(q.difficulty);
      questionStartRef.current = Date.now();
      setTimeLeft(QUESTION_TIMEOUT_MS);
      setTimeout(() => setPhase(PH.PLAYER_QUESTION), 600);
    }
  }

  // ── player clicks FLEE ────────────────────────────────────────────────────
  function handleFlee() {
    addLog("You retreated — regroup and challenge again!");
    setPhase(PH.ZONE_SELECT);
  }

  // ── question submitted (ATTACK or DEFEND) ─────────────────────────────────
  function handleSubmit(e) {
    e?.preventDefault();
    clearInterval(timerRef.current);
    const elapsed    = Date.now() - questionStartRef.current;
    const userAnswer = parseFloat(inputVal.trim());
    const correct    = !isNaN(userAnswer) && userAnswer === question.answer;

    if (correct) {
      setCorrectCount(c => c + 1);
      setTotalMs(t => t + elapsed);
    }

    if (phase === PH.PLAYER_QUESTION) {
      resolvePlayerAttack(correct, elapsed);
    } else {
      resolveEnemyAttack(correct, elapsed);
    }
    setInputVal("");
  }

  // ── timed out ─────────────────────────────────────────────────────────────
  function handleTimeout() {
    const elapsed = QUESTION_TIMEOUT_MS;
    if (phase === PH.PLAYER_QUESTION) resolvePlayerAttack(false, elapsed);
    else resolveEnemyAttack(false, elapsed);
    setInputVal("");
  }

  // ── resolve PLAYER ATTACK ─────────────────────────────────────────────────
  function resolvePlayerAttack(correct, elapsed) {
    setPhase(PH.ANIMATING);
    let attackStat = player.attack;

    // MULTIPLY doubles attack
    if (hasEffect(player, EFFECTS.MULTIPLY)) {
      attackStat *= 2;
      setPlayer(prev => removeStatusEffect(prev, EFFECTS.MULTIPLY));
    }

    let damage = calculateDamage(attackStat, correct, elapsed);

    // DIVIDE halves enemy defence (already applied to enemy activeEffects)
    let effectiveDef = enemy.defence;
    if (hasEffect(enemy, EFFECTS.DIVIDE)) {
      effectiveDef = Math.floor(effectiveDef / 2);
      setEnemy(prev => removeStatusEffect(prev, EFFECTS.DIVIDE));
    }
    // Net damage after defence
    const netDmg = Math.max(0, damage - Math.floor(effectiveDef * 0.3));

    if (!correct) {
      addLog(`Wrong answer — ${enemy.name} evades your strike!`);
      flash("MISS!", C.red);
    } else {
      addLog(`Correct! Your answer strikes ${enemy.name} for ${netDmg} damage!`);
      flash(`-${netDmg}`, C.red);
      setEnemyShake(true);
      setTimeout(() => { setEnemyShake(false); }, 700);
    }

    const newEnemyHp = Math.max(0, enemy.hp - netDmg);
    setEnemy(prev => ({ ...prev, hp: newEnemyHp }));

    const outcome = checkWinCondition(player.hp, newEnemyHp);
    if (outcome === "win") {
      setTimeout(() => resolveVictory(), 900);
    } else {
      // enemy turn
      setTimeout(() => startEnemyTurn(), 900);
    }
  }

  // ── enemy turn — generate a question for the player to DEFEND ────────────
  function startEnemyTurn() {
    // Use the enemy's topic and yearGroup for the defence question
    const topicOverride = hasEffect(enemy, EFFECTS.NEGATIVE) ? "addition" : enemy.topic;
    const q = generateQuestion(enemy.yearGroup, topicOverride);
    setQuestion(q);
    setInputVal("");
    questionStartRef.current = Date.now();
    setTimeLeft(QUESTION_TIMEOUT_MS);
    setPhase(PH.ENEMY_QUESTION);
      addLog(`${enemy.name} strikes back! Solve the question to shield yourself!`);
  }

  // ── resolve ENEMY ATTACK (player defended) ────────────────────────────────
  function resolveEnemyAttack(correct, elapsed) {
    setPhase(PH.ANIMATING);
    const block  = calculateBlock(player.defence, correct);
    let rawDmg   = enemy.attack;

    // PRIME_SHIELD: if answer is prime, block everything
    if (hasEffect(player, EFFECTS.PRIME_SHIELD) && isPrime(question.answer)) {
      addLog(`Prime Shield activated — all damage deflected!`);
      flash("PRIME SHIELD!", C.purple);
      setPlayer(prev => removeStatusEffect(prev, EFFECTS.PRIME_SHIELD));
      setTimeout(() => nextPlayerTurn(), 800);
      return;
    }

    const netDmg = Math.max(0, rawDmg - block);

    if (correct) {
      addLog(`Shielded! Your answer absorbs the blow — only ${netDmg} damage taken.`);
      flash(`-${netDmg}`, hpColor(hpPct(player.hp - netDmg, player.maxHp)));
    } else {
      addLog(`Wrong! ${enemy.name} breaks through for ${netDmg} damage!`);
      flash(`-${netDmg}`, C.red);
    }

    setPlayerShake(true);
    setTimeout(() => { setPlayerShake(false); }, 700);

    const newPlayerHp = Math.max(0, player.hp - netDmg);
    setPlayer(prev => ({ ...prev, hp: newPlayerHp }));

    const outcome = checkWinCondition(newPlayerHp, enemy.hp);
    if (outcome === "lose") {
      setTimeout(() => resolveDefeat(), 900);
    } else {
      setTimeout(() => nextPlayerTurn(), 900);
    }
  }

  function nextPlayerTurn() {
    // Tick any lingering enemy effects off
    setEnemy(prev => removeStatusEffect(prev, EFFECTS.NEGATIVE));
    setPhase(PH.PLAYER_CHOICE);
  }

  // ── victory / defeat ──────────────────────────────────────────────────────
  function resolveVictory() {
    const xp = enemy.xpReward + awardXP(correctCount, lastDifficulty, totalMs);
    setXpEarned(xp);
    addLog(`Maths mastered! ${enemy.name} is defeated!`);
    setPhase(PH.VICTORY);
    if (onXPEarned) onXPEarned(xp);
  }

  function resolveDefeat() {
    addLog("The numbers beat you this time — study and return!");
    setPhase(PH.DEFEAT);
  }

  function fightAgain() {
    startBattle(zone);
  }

  // ── keyboard shortcut: Enter submits ──────────────────────────────────────
  const handleKey = useCallback((e) => {
    if (e.key === "Enter" && (phase === PH.PLAYER_QUESTION || phase === PH.ENEMY_QUESTION)) {
      handleSubmit();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, inputVal, question]);

  useEffect(() => {
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [handleKey]);

  // ─────────────────────────────────────────────────────────────────────────
  //  RENDER
  // ─────────────────────────────────────────────────────────────────────────

  // ── Zone Select ──────────────────────────────────────────────────────────
  if (phase === PH.ZONE_SELECT) {
    return (
      <div style={{ fontFamily: NU, color: C.text }}>
        {/* Header */}
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:20 }}>
          <button onClick={onBack} style={{ ...btnStyle(C.bgFlat), display:"flex", alignItems:"center", gap:6 }}>
            <img src="/icon-exit.png" alt="" style={{ width:16, height:16, imageRendering:"pixelated" }} /> EXIT
          </button>
          <div style={{ fontFamily:PX, fontSize:12, color:C.gold }}>Choose Your Zone</div>
        </div>

        {/* Zone grid */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(240px,1fr))", gap:14 }}>
          {ZONES.map(z => (
            <button key={z.id} onClick={() => startBattle(z)} style={{
              background: C.bgCard,
              border: `4px solid ${z.theme}55`,
              boxShadow: `5px 5px 0 ${C.shadow}, 0 0 18px ${z.theme}33`,
              padding: "18px 16px",
              textAlign: "left",
              cursor: "pointer",
              color: C.text,
              transition: "transform 0.12s, box-shadow 0.12s",
            }}
            onMouseEnter={e => { e.currentTarget.style.transform="scale(1.03) translateY(-3px)"; e.currentTarget.style.borderColor=z.theme; }}
            onMouseLeave={e => { e.currentTarget.style.transform="scale(1)"; e.currentTarget.style.borderColor=`${z.theme}55`; }}
            >
              <div style={{ fontFamily:PX, fontSize:9, color:z.theme, marginBottom:8, letterSpacing:"0.08em" }}>{z.name}</div>
              <div style={{ fontSize:11, color:C.textSub, fontWeight:700, marginBottom:10 }}>
                KS{z.ks} · Year {z.enemies[0].yearGroup}–{z.enemies[z.enemies.length-1].yearGroup}
              </div>
              <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:10, alignItems:"center" }}>
                {z.enemies.map(en => (
                  <img key={en.id} src={en.sprite} alt={en.name} title={en.name}
                    style={{ width:48, height:48, imageRendering:"pixelated" }} />
                ))}
                <img src={z.boss.sprite} alt={z.boss.name} title={`Boss: ${z.boss.name}`}
                  style={{ width:32, height:32, imageRendering:"pixelated",
                    filter:`drop-shadow(0 0 6px ${z.theme})` }} />
              </div>
              <div style={{ fontFamily:PX, fontSize:7, color:C.textDim }}>
                Boss: {z.boss.name}
              </div>
              <div style={{ marginTop:10, padding:"4px 10px", border:`2px solid ${z.theme}`, display:"inline-block",
                fontFamily:PX, fontSize:7, color:z.theme }}>
                ENTER ZONE →
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ── Battle Screens ────────────────────────────────────────────────────────
  if (!player || !enemy) return null;

  const playerHpPct = hpPct(player.hp, player.maxHp);
  const enemyHpPct  = hpPct(enemy.hp,  enemy.maxHp);
  const isQPhase    = phase === PH.PLAYER_QUESTION || phase === PH.ENEMY_QUESTION;
  const timerPct    = (timeLeft / QUESTION_TIMEOUT_MS) * 100;
  const isDefending = phase === PH.ENEMY_QUESTION;
  const diffLabel   = isDefending ? "SHIELD YOUR HP!" : "STRIKE WITH MATHS!";
  const diffColor   = isDefending ? C.red : C.green;

  // ── Victory / Defeat ─────────────────────────────────────────────────────
  if (phase === PH.VICTORY || phase === PH.DEFEAT) {
    const won = phase === PH.VICTORY;
    return (
      <div style={{ fontFamily: NU, color: C.text, textAlign:"center" }}>
        <div style={{ ...cardStyle, padding:"40px 24px" }}>
          {/* Big emoji / sprite */}
          <div style={{ fontSize:64, marginBottom:16 }}>
            <img
            src={won ? "/icon-victory.png" : "/icon-defeat.png"}
            alt={won ? "Victory" : "Defeat"}
            style={{ width:120, height:120, imageRendering:"pixelated",
              filter:`drop-shadow(0 0 24px ${won ? C.gold : C.red}aa)` }}
          />
          </div>
          <div style={{ fontFamily:PX, fontSize:16, color: won ? C.gold : C.red,
            textShadow:`0 0 24px ${won ? C.gold : C.red}88`, marginBottom:8 }}>
            {won ? "VICTORY!" : "DEFEATED!"}
          </div>
          <div style={{ fontSize:14, color:C.textSub, fontWeight:700, marginBottom:24 }}>
            {won ? `You outthought the ${enemy.name} with your maths skills!` : "Your equations weren't sharp enough — practise and try again!"}
          </div>

          {won && (
            <div style={{ background:C.bgFlat, border:`2px solid ${C.gold}`, padding:"16px 20px",
              display:"inline-block", marginBottom:24, minWidth:200 }}>
              <div style={{ fontFamily:PX, fontSize:8, color:C.textDim, marginBottom:8 }}>XP EARNED</div>
              <div style={{ fontFamily:PX, fontSize:20, color:C.gold }}>+{xpEarned}</div>
            </div>
          )}

          {/* Log */}
          <div style={{ maxWidth:400, margin:"0 auto 24px", textAlign:"left" }}>
            {combatLog.slice(0,4).map((msg, i) => (
              <div key={i} style={{ fontSize:12, color: i === 0 ? C.text : C.textDim,
                fontWeight:700, padding:"3px 0", borderBottom:`1px solid ${C.border}` }}>
                {msg}
              </div>
            ))}
          </div>

          <div style={{ display:"flex", gap:12, justifyContent:"center", flexWrap:"wrap" }}>
            <button onClick={fightAgain} style={{ ...btnStyle(C.purpleMid), display:"flex", alignItems:"center", gap:8 }}>
              <img src="/icon-attack.png" alt="" style={{ width:18, height:18, imageRendering:"pixelated" }} /> CHALLENGE AGAIN
            </button>
            <button onClick={() => setPhase(PH.ZONE_SELECT)} style={{ ...btnStyle(C.bgFlat), display:"flex", alignItems:"center", gap:8 }}>
              <img src="/icon-zones.png" alt="" style={{ width:18, height:18, imageRendering:"pixelated" }} /> PICK ZONE
            </button>
            <button onClick={onBack} style={{ ...btnStyle(C.bgFlat), display:"flex", alignItems:"center", gap:8 }}>
              <img src="/icon-exit.png" alt="" style={{ width:18, height:18, imageRendering:"pixelated" }} /> EXIT
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Main Battle UI ────────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: NU, color: C.text, position:"relative" }}>

      {/* Flash message overlay */}
      {flashMsg && (
        <div style={{
          position:"absolute", top:"38%", left:"50%", transform:"translate(-50%,-50%)",
          fontFamily:PX, fontSize:20, color:flashMsg.color, pointerEvents:"none", zIndex:20,
          textShadow:`0 0 24px ${flashMsg.color}`, animation:"flashFade 1.2s ease-out forwards",
        }}>
          {flashMsg.text}
        </div>
      )}

      {/* ══ BATTLEFIELD ══════════════════════════════════════════════════════ */}
      <div style={{
        position:"relative", height:260, overflow:"hidden",
        background: ZONE_GRADIENT[zone.id] || C.bgAlt,
        border:`3px solid ${zone.theme}88`,
        borderBottom:"none",
      }}>
        {/* dim overlay when question is active */}
        <div style={{ position:"absolute", inset:0, background: isQPhase ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.1)", transition:"background 0.3s" }} />

        {/* Ground band — solid floor sprites stand on */}
        <div style={{
          position:"absolute", bottom:44, left:0, right:0, height:22,
          background: ZONE_GROUND[zone.id] || "#111",
          borderTop:`2px solid ${zone.theme}55`,
          zIndex:1,
        }} />

        {/* Zone name + Retreat */}
        <div style={{ position:"absolute", top:0, left:0, right:0, display:"flex", justifyContent:"space-between", alignItems:"center",
          padding:"8px 12px", background:"rgba(0,0,0,0.55)", zIndex:2 }}>
          <div style={{ fontFamily:PX, fontSize:7, color:zone.theme, letterSpacing:"0.08em" }}>{zone.name}</div>
          <button onClick={handleFlee} style={{ ...btnStyle(C.bgFlat), fontSize:9, padding:"5px 10px", display:"flex", alignItems:"center", gap:5 }}>
            <img src="/icon-flee.png" alt="" style={{ width:13, height:13, imageRendering:"pixelated" }} /> RETREAT
          </button>
        </div>

        {/* ENEMY — upper right */}
        <div style={{ position:"absolute", bottom:44, right:24, textAlign:"center", zIndex:2 }}>
          <img
            src={enemy.sprite} alt={enemy.name}
            style={{
              width:160, height:160, imageRendering:"pixelated",
              filter: enemyShake
                ? `drop-shadow(0 0 18px ${C.red}) brightness(1.8)`
                : `drop-shadow(0 0 12px ${zone.theme}88)`,
              animation: enemyShake ? "shake 0.4s ease" : "none",
              display:"block",
            }}
          />
        </div>

        {/* PLAYER — lower left */}
        <div style={{ position:"absolute", bottom:44, left:24, textAlign:"center", zIndex:2 }}>
          <img
            src={`/char-${charTemplate.id}.png`} alt={charTemplate.name}
            style={{
              width:160, height:160, imageRendering:"pixelated",
              filter:`drop-shadow(0 0 14px ${charTemplate.color}99)${playerShake ? " brightness(2)" : ""}`,
              animation: playerShake ? "shake 0.4s ease" : "none",
              display:"block",
            }}
          />
        </div>

        {/* Combat log — bottom strip */}
        <div style={{ position:"absolute", bottom:0, left:0, right:0, zIndex:3,
          background:"rgba(0,0,0,0.82)", borderTop:`2px solid ${C.border}55`,
          padding:"6px 14px" }}>
          <div style={{ fontSize:12, color:C.text, fontWeight:700, lineHeight:1.5 }}>{combatLog[0]}</div>
        </div>
      </div>

      {/* ══ HP BARS ══════════════════════════════════════════════════════════ */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, background:C.bgAlt,
        border:`2px solid ${C.border}`, borderTop:"none", padding:"10px 14px" }}>

        {/* Player HP */}
        <div>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:4 }}>
            <span style={{ fontFamily:PX, fontSize:7, color:charTemplate.color }}>{charTemplate.name}</span>
            <span style={{ fontFamily:PX, fontSize:7, color:hpColor(playerHpPct) }}>{player.hp}/{player.maxHp} HP</span>
          </div>
          <div style={{ background:C.bgFlat, border:`2px solid ${C.border}`, height:12, position:"relative" }}>
            <div style={{ height:"100%", width:`${playerHpPct}%`, background:hpColor(playerHpPct),
              boxShadow:`0 0 8px ${hpColor(playerHpPct)}88`, transition:"width 0.4s ease, background 0.4s" }} />
          </div>
          {player.activeEffects?.length > 0 && (
            <div style={{ fontSize:10, color:C.purple, marginTop:3, fontWeight:700 }}>{player.activeEffects.map(e=>`[${e}]`).join(" ")}</div>
          )}
        </div>

        {/* Enemy HP */}
        <div>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:4 }}>
            <span style={{ fontFamily:PX, fontSize:7, color:"#f97316" }}>{enemy.name}</span>
            <span style={{ fontFamily:PX, fontSize:7, color:enemyHpColor(enemyHpPct) }}>{enemy.hp}/{enemy.maxHp} HP</span>
          </div>
          <div style={{ background:C.bgFlat, border:`2px solid ${C.border}`, height:12, position:"relative" }}>
            <div style={{ height:"100%", width:`${enemyHpPct}%`, background:enemyHpColor(enemyHpPct),
              boxShadow:`0 0 8px ${enemyHpColor(enemyHpPct)}88`, transition:"width 0.4s ease, background 0.4s" }} />
          </div>
          {enemy.activeEffects?.length > 0 && (
            <div style={{ fontSize:10, color:C.purple, marginTop:3, fontWeight:700 }}>{enemy.activeEffects.map(e=>`[${e}]`).join(" ")}</div>
          )}
        </div>
      </div>

      {/* ══ QUESTION PANEL — dramatic full-width overlay card ════════════════ */}
      {isQPhase && question && (
        <div style={{
          background: isDefending ? C.redBg : C.bgCard,
          border:`4px solid ${diffColor}`,
          boxShadow:`0 0 32px ${diffColor}66`,
          padding:"20px 24px",
          marginTop:2,
        }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
            <div style={{ fontFamily:PX, fontSize:9, color:diffColor, letterSpacing:"0.1em" }}>{diffLabel}</div>
            {/* Timer bar */}
            <div style={{ flex:1, marginLeft:16, background:C.bgFlat, border:`2px solid ${C.border}`, height:8 }}>
              <div style={{ height:"100%", width:`${timerPct}%`,
                background: timerPct > 50 ? C.green : timerPct > 25 ? C.gold : C.red,
                transition:"width 0.1s linear" }} />
            </div>
          </div>
          <div style={{ fontFamily:PX, fontSize:20, color:C.gold, textAlign:"center",
            marginBottom:20, lineHeight:1.8, textShadow:`0 0 20px ${C.gold}88` }}>
            {question.question}
          </div>
          <form onSubmit={handleSubmit} style={{ display:"flex", gap:12, justifyContent:"center" }}>
            <input
              ref={inputRef} type="number" value={inputVal}
              onChange={e => setInputVal(e.target.value)}
              placeholder="?" autoComplete="off" inputMode="numeric"
              style={{ width:110, height:54, border:`4px solid ${diffColor}`, background:C.bgAlt,
                color:C.gold, fontFamily:PX, fontSize:20, textAlign:"center", outline:"none",
                boxShadow:`3px 3px 0 ${C.shadow}` }}
            />
            <button type="submit" style={{ ...btnStyle(isDefending ? C.red : C.purpleMid), padding:"12px 28px", fontSize:13, letterSpacing:"0.05em" }}>
              {isDefending ? "SHIELD!" : "SOLVE!"}
            </button>
          </form>
        </div>
      )}

      {/* ══ ACTION BUTTONS ═══════════════════════════════════════════════════ */}
      {phase === PH.PLAYER_CHOICE && (
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginTop:10 }}>

          {/* SOLVE TO STRIKE */}
          <button onClick={handleAttack} style={{
            ...btnStyle(C.purpleMid), padding:"18px 14px",
            display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:10,
          }}>
            <img src="/icon-attack.png" alt="" style={{
              width:40, height:40, imageRendering:"pixelated", mixBlendMode:"screen",
            }} />
            <span style={{ fontSize:10, letterSpacing:"0.06em" }}>SOLVE TO STRIKE</span>
          </button>

          {/* ABILITY */}
          <button onClick={handleAbility} disabled={abilityUsed} style={{
            ...btnStyle(abilityUsed ? C.bgFlat : charTemplate.color), padding:"18px 14px",
            display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:10,
            opacity: abilityUsed ? 0.45 : 1, cursor: abilityUsed ? "not-allowed" : "pointer",
            color: abilityUsed ? C.textDim : "#fff",
          }}>
            <img src="/icon-ability.png" alt="" style={{
              width:40, height:40, imageRendering:"pixelated", mixBlendMode:"screen",
              opacity: abilityUsed ? 0.4 : 1,
            }} />
            <span style={{ fontSize:10, letterSpacing:"0.06em" }}>
              {charTemplate.ability.name.toUpperCase()}{abilityUsed ? " (USED)" : ""}
            </span>
          </button>

        </div>
      )}

      {phase === PH.ANIMATING && (
        <div style={{ marginTop:10, textAlign:"center", fontFamily:PX, fontSize:8, color:C.textDim, padding:"10px" }}>
          . . .
        </div>
      )}

      {/* Inline keyframe for flash + shake */}
      <style>{`
        @keyframes flashFade {
          0%   { opacity:1; transform:translate(-50%,-50%) scale(1.3); }
          70%  { opacity:0.8; }
          100% { opacity:0; transform:translate(-50%,-70%) scale(1); }
        }
        @keyframes shake {
          0%,100% { transform:translateX(0); }
          20%     { transform:translateX(-6px); }
          40%     { transform:translateX(6px); }
          60%     { transform:translateX(-4px); }
          80%     { transform:translateX(4px); }
        }
      `}</style>
    </div>
  );
}

// ─── Shared micro-styles ──────────────────────────────────────────────────────
const cardStyle = {
  background:  C.bgCard,
  border:      `4px solid ${C.border}`,
  boxShadow:   `5px 5px 0 ${C.shadow}, 0 0 20px ${C.border}66`,
  padding:     20,
  marginBottom:0,
};

const flatStyle = {
  background: C.bgFlat,
  border:     `2px solid ${C.border}`,
  padding:    "10px 14px",
};

function btnStyle(bg = C.purpleMid) {
  return {
    border:     `4px solid ${C.border}`,
    padding:    "10px 16px",
    background: bg,
    color:      "#fff",
    fontFamily: PX,
    fontSize:   9,
    lineHeight: 1.8,
    boxShadow:  `5px 5px 0 ${C.shadow}`,
    cursor:     "pointer",
  };
}
